import { z } from 'zod';

/**
 * Override type enum
 */
export const overrideTypeSchema = z.enum([
  'position',
  'rotation',
  'scale',
  'color',
  'material',
  'plate_assignment',
]);

/**
 * Position override schema
 */
export const positionOverrideSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * Rotation override schema (radians)
 */
export const rotationOverrideSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * Scale override schema (0.1x to 10x)
 */
export const scaleOverrideSchema = z.object({
  x: z.number().min(0.1).max(10),
  y: z.number().min(0.1).max(10),
  z: z.number().min(0.1).max(10),
});

/**
 * Color override schema (hex color)
 */
export const colorOverrideSchema = z.object({
  hex: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color (#RRGGBB)'),
});

/**
 * Material override schema
 */
export const materialOverrideSchema = z.object({
  pid: z.string().min(1),
});

/**
 * Plate assignment override schema
 */
export const plateAssignmentOverrideSchema = z.object({
  targetBuildPlateId: z.string().uuid(),
});

/**
 * Apply override request schema
 */
export const applyOverrideSchema = z.object({
  type: overrideTypeSchema,
  position: positionOverrideSchema.optional(),
  rotation: rotationOverrideSchema.optional(),
  scale: scaleOverrideSchema.optional(),
  color: colorOverrideSchema.optional(),
  material: materialOverrideSchema.optional(),
  plateAssignment: plateAssignmentOverrideSchema.optional(),
  reason: z.string().max(500).optional(),
}).refine(
  (data) => {
    // Ensure the correct data field is provided for the override type
    switch (data.type) {
      case 'position':
        return data.position !== undefined;
      case 'rotation':
        return data.rotation !== undefined;
      case 'scale':
        return data.scale !== undefined;
      case 'color':
        return data.color !== undefined;
      case 'material':
        return data.material !== undefined;
      case 'plate_assignment':
        return data.plateAssignment !== undefined;
      default:
        return false;
    }
  },
  {
    message: 'Override data must match the specified type',
  }
);

/**
 * Inferred types
 */
export type OverrideType = z.infer<typeof overrideTypeSchema>;
export type PositionOverride = z.infer<typeof positionOverrideSchema>;
export type RotationOverride = z.infer<typeof rotationOverrideSchema>;
export type ScaleOverride = z.infer<typeof scaleOverrideSchema>;
export type ColorOverride = z.infer<typeof colorOverrideSchema>;
export type MaterialOverride = z.infer<typeof materialOverrideSchema>;
export type PlateAssignmentOverride = z.infer<typeof plateAssignmentOverrideSchema>;
export type ApplyOverrideInput = z.infer<typeof applyOverrideSchema>;
